import { CSSProperties, useState, useEffect } from 'react'
import { SummaryCardPayload } from 'Hooks/useMaestroChat'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import { getPalette } from 'react-palette'


const AssistantSummaryCard = ({ summaryCard }: { summaryCard: SummaryCardPayload }) => {
    const [isHovered, setIsHovered] = useState(false)
    const [backgroundColor, setBackgroundColor] = useState<string>('white')
    const [imageAspectRatio, setImageAspectRatio] = useState<'square' | 'wide' | 'tall'>('square')
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)

    useEffect(() => {
        if (summaryCard.imageUrl) {
            const img = new Image()
            img.onload = () => {
                const aspectRatio = img.naturalWidth / img.naturalHeight
                setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight })

                if (aspectRatio > 1.5) {
                    setImageAspectRatio('wide')
                } else if (aspectRatio < 0.75) {
                    setImageAspectRatio('tall')
                } else {
                    setImageAspectRatio('square')
                }
            }
            img.src = summaryCard.imageUrl

            // Also get the color palette
            getPalette(summaryCard.imageUrl)
                .then(palette => {
                    const bgColor = palette.lightVibrant || palette.muted || 'white'
                    setBackgroundColor(bgColor)
                })
                .catch(error => {
                    console.error('Failed to get palette:', error)
                    setBackgroundColor('white')
                })
        }
    }, [summaryCard.imageUrl])

    const isWideLayout = imageAspectRatio === 'wide'

    const cardStyle: CSSProperties = {
        ...sty.summaryCard,
        backgroundColor,
        ...(isWideLayout && sty.summaryCard_wide),
        ...(summaryCard.linkUrl && isHovered && {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            cursor: 'pointer'
        }),
    }

    const imageStyle: CSSProperties = {
        ...(imageAspectRatio === 'wide' ? sty.summaryCardImage_wide :
            imageAspectRatio === 'tall' ? sty.summaryCardImage_tall :
                sty.summaryCardImage)
    }

    const cardContent = (
        <div
            className="assistantSummaryCard"
            style={cardStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {summaryCard.imageUrl && (
                <img
                    src={summaryCard.imageUrl}
                    alt={summaryCard.title}
                    style={imageStyle}
                />
            )}
            <div style={sty.summaryCardContent}>
                <div style={sty.summaryCardTitle}>{summaryCard.title}</div>
                <div style={sty.summaryCardSubtitle}>{summaryCard.subtitle}</div>
            </div>
        </div>
    )

    if (summaryCard.linkUrl) {
        return (
            <a href={summaryCard.linkUrl} target="_blank" rel="noopener noreferrer" style={sty.summaryCardLink}>
                {cardContent}
            </a>
        )
    }

    return cardContent
}

export default AssistantSummaryCard

const sty: Record<string, CSSProperties> = {
    summaryCard: {
        display: 'flex',
        border: `1px solid ${Colors.grey}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'white',
        transition: 'all 0.2s ease',
        maxWidth: 400,
    },
    summaryCard_wide: {
        flexDirection: 'column',
        maxWidth: 300,
    },
    summaryCardLink: {
        textDecoration: 'none',
        color: 'inherit',
    },
    summaryCardImage: {
        width: 80,
        height: 80,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardImage_wide: {
        width: '100%',
        height: 120,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardImage_tall: {
        width: 60,
        height: 100,
        objectFit: 'cover',
        flexShrink: 0,
    },
    summaryCardContent: {
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
    },
    summaryCardTitle: {
        fontWeight: 'bold',
        fontSize: 16,
        marginBottom: 4,
        color: Colors.textColor,
    },
    summaryCardSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },
}